/**
 * customer-app · 订单列表持久层
 *
 * 来源：js/order-lib.js（已迁移）。
 *
 * 职责：仅读写 `localStorage.restaurantOrders`，并派发跨页同步事件。
 *
 * 兼容契约（必须保留，被 app.js / ui-controller.js / admin-app.js / customer-lite.js 调用）：
 *   - `window.RestaurantOrderLib`：{ ORDERS_KEY, getOrders, saveOrders, addOrder }
 *   - `OS.registerModule('orders', …)`
 *   - 顶层副作用：监听 'storage' 事件，跨标签同步派发 'restaurantOrdersUpdated'
 *   - saveOrders / addOrder 写入后派发 'restaurantOrdersUpdated' CustomEvent，detail = list
 *
 * 设计：
 *   - `OrderRecord = Record<string, unknown>`：开放契约，因 order 字段在不同业务里不一
 *   - `getOrders / saveOrders / addOrder` 提供 `<T>` 泛型扩展点，调用方可自定义类型
 *   - 须在 app.js 之前加载（顶层 `addEventListener('storage', …)` 必须早注册）
 */

/* ─────────── 类型 ─────────── */

export type OrderRecord = Record<string, unknown>;

export interface RestaurantOrderLib {
  readonly ORDERS_KEY: 'restaurantOrders';
  getOrders<T = OrderRecord>(): T[];
  saveOrders<T = OrderRecord>(list: T[]): void;
  addOrder<T = OrderRecord>(order: T): void;
}

/* ─────────── 全局 facade 局部断言（不污染 customer-bootstrap 全局类型） ─────────── */

interface RuntimeGlobal {
  RestaurantOrderLib?: RestaurantOrderLib;
  OS?: {
    registerModule?: (name: string, ref: unknown) => unknown;
  };
  localStorage: Storage;
  dispatchEvent(event: Event): boolean;
  addEventListener(type: 'storage', listener: (e: StorageEvent) => void): void;
}

/** 通过 unknown 中转，避免 `typeof globalThis` 与 `Window` 的字段不重叠告警 */
const G = (typeof window !== 'undefined' ? window : globalThis) as unknown as RuntimeGlobal;

const ORDERS_KEY = 'restaurantOrders' as const;

/* ─────────── 内部 IO ─────────── */

const readList = <T = OrderRecord>(): T[] => {
  try {
    const raw = G.localStorage.getItem(ORDERS_KEY);
    if (raw === null || raw === '') return [];
    const o: unknown = JSON.parse(raw);
    return Array.isArray(o) ? (o as T[]) : [];
  } catch {
    return [];
  }
};

const dispatchUpdated = <T = OrderRecord>(list: T[]): void => {
  G.dispatchEvent(new CustomEvent('restaurantOrdersUpdated', { detail: list }));
};

const writeList = <T = OrderRecord>(arr: T[] | unknown): void => {
  const list = (Array.isArray(arr) ? arr : []) as T[];
  try {
    G.localStorage.setItem(ORDERS_KEY, JSON.stringify(list));
  } catch (e) {
    /* localStorage 写入失败（私密模式 / 配额）不致命，但要日志保留 */
    if (typeof console !== 'undefined') console.warn('order-lib 写入失败:', e);
  }
  dispatchUpdated(list);
};

/* ─────────── 跨标签同步：另一标签写入时本标签也派发事件 ─────────── */

G.addEventListener('storage', (e: StorageEvent): void => {
  if (e.key !== ORDERS_KEY || e.newValue == null) return;
  try {
    const o: unknown = JSON.parse(e.newValue);
    if (Array.isArray(o)) dispatchUpdated(o as OrderRecord[]);
  } catch {
    /* ignore：其他标签写入了非法 JSON，本标签忽略 */
  }
});

/* ─────────── 公开 API ─────────── */

const getOrders = <T = OrderRecord>(): T[] => readList<T>();

const saveOrders = <T = OrderRecord>(list: T[]): void => {
  writeList<T>(list);
};

const addOrder = <T = OrderRecord>(order: T): void => {
  const list = readList<T>();
  list.unshift(order);
  writeList<T>(list);
};

/* ─────────── 暴露 ─────────── */

const lib: RestaurantOrderLib = {
  ORDERS_KEY,
  getOrders,
  saveOrders,
  addOrder,
};

G.RestaurantOrderLib = lib;

if (typeof G.OS?.registerModule === 'function') {
  try {
    G.OS.registerModule('orders', lib);
  } catch {
    /* OS facade 注册失败不致命 */
  }
}
